// PostHog client bootstrap. Sends pageviews plus explicitly tracked events only.
//
// Adding an event:
//   Client: import { track } from 'app/modules/analytics' in a Stimulus controller, then
//           track('area:action', { ...props })
//   Server: track_event('area:action', { ...props }) in a controller action (AnalyticsTrackable)
//
// Names follow area:action (dashboard:chart_template_create)
import posthog from 'posthog-js'

let deniedParams = null

const URL_PROPERTY = /(?:url|referrer)$/i

function isDenied(name) {
    if (!deniedParams) return false

    return deniedParams.some((denied) => name.toLowerCase().includes(denied))
}

function isReserved(name) {
    return name === 'token' || name.startsWith('$')
}

// With no list, drop every query param rather than risk shipping a sensitive one.
function isDeniedParam(param) {
    return deniedParams ? isDenied(param) : true
}

function scrubUrl(raw) {
    try {
        const u = new URL(raw)
        ;[...u.searchParams.keys()].forEach((k) => {
            if (isDeniedParam(k)) u.searchParams.delete(k)
        })
        return u.toString()
    } catch {
        return raw
    }
}

function scrubProperties(props) {
    if (!props) return
    for (const [key, value] of Object.entries(props)) {
        if (!isReserved(key) && isDenied(key)) delete props[key]
        else if (typeof value === 'string' && URL_PROPERTY.test(key)) props[key] = scrubUrl(value)
    }
}

export function scrub(event) {
    if (!event) return event
    const p = event.properties || {}
    scrubProperties(p)
    scrubProperties(p.$set)
    scrubProperties(p.$set_once)
    return event
}

let started = false

// Called on every turbo:load. posthog.init() runs once, but identity must reconcile on
// every visit.
export function initAnalytics() {
    const cfg = window.posthogBootstrap
    if (!cfg) return

    if (!started) {
        started = true
        initPosthog(cfg)
    }

    // Clear any prior user's identity on logout
    if (cfg.reset) posthog.reset()

    // app_env + product on every event
    // Registered after reset() (which wipes super properties) so logout pageviews keep them.
    if (cfg.super_properties) posthog.register(cfg.super_properties)

    if (cfg.distinct_id) {
        // identify() here does one job: merge the prior anonymous session into this person.
        if (posthog.get_distinct_id() !== cfg.distinct_id) {
            posthog.identify(cfg.distinct_id)
        }

        if (cfg.identified_super_properties) posthog.register(cfg.identified_super_properties)

        // Re-group on company switch (a Turbo redirect, so it lands here, not in init)
        if (cfg.group && posthog.getGroups()[cfg.group.type] !== cfg.group.key) {
            posthog.group(cfg.group.type, cfg.group.key, cfg.group.properties)
        }
    }
}

function initPosthog(cfg) {
    deniedParams = cfg.denied_params || null // must precede init(): before_send fires from it

    posthog.init(cfg.key, {
        api_host: cfg.host,
        person_profiles: 'identified_only', // anonymous funnel still captured; profile created on identify
        // posthog-js listens to the History API, which Turbo Drive uses
        capture_pageview: 'history_change',
        // Only auto-track pageviews plus explicit events: the automatic collectors add excessive
        // usage
        capture_pageleave: false,
        autocapture: false,
        capture_dead_clicks: false,
        capture_heatmaps: false,
        // Masks the values of known ad-click IDs (gclid, fbclid, ...) inside captured URLs.
        mask_personal_data_properties: true,
        property_denylist: [
            'card_number',
            'cvv',
            'api_key',
            'authentication_token',
            'secret_key',
            'key',
            'postback_key',
        ],
        session_recording: { maskAllInputs: true },
        disable_session_recording: true, // off for now: volume + PII; revisit deliberately
        before_send: scrub,
    })
}

export function track(name, props = {}) {
    if (!started || !name) return
    posthog.capture(name, props)
}
